"use client";

import {
  Users,
  BookOpen,
  CheckCircle,
  User,
  Heart,
  Globe,
  Target,
  Star,
  Eye,
  ArrowBigRight,
} from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useEffect, useState } from "react";
import { BiWorld } from "react-icons/bi";
import { BsPeople } from "react-icons/bs";
import { useRouter } from "next/navigation";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, Navigation } from "swiper/modules";
import Image from "next/image";
import { brands } from "@/components/landing/dummy";

const coreValues = [
  {
    title: "Excellence",
    description:
      "We strive for the highest standards in education delivery and student outcomes.",
    icon: Star,
  },
  {
    title: "Accessibility",
    description:
      "Making quality education available to everyone, regardless of location or background.",
    icon: Globe,
  },
  {
    title: "Innovation",
    description:
      "Embracing cutting-edge technology to enhance the learning experience.",
    icon: BookOpen,
  },
  {
    title: "Integrity",
    description:
      "Building trust through transparency, honesty, and ethical practices.",
    icon: CheckCircle,
  },
  {
    title: "Community",
    description:
      "Fostering collaboration and support among learners, educators, and partners.",
    icon: Users,
  },
  {
    title: "Empowerment",
    description:
      "Equipping individuals with skills and knowledge to transform their lives.",
    icon: Target,
  },
];

const communityRoles = [
  {
    title: "Volunteer",
    description:
      "Support our mission by volunteering your time and skills to help students succeed in their learning journey.",
    icon: Heart,
    iconBg: "bg-red-100",
    buttonText: "Get Promoted to Facilitator Support Associate",
    buttonColor: "bg-red-500 hover:bg-red-600",
    features: [
      "Get an appointment letter",
      "Gain valuable experience",
      "Get Reference",
      "Get Volunteer Certificate",
      "Earn Money",
      "Attract potential employers",
      "Personalized dashboard",
      "Global recognition",
    ],
  },
  {
    title: "Facilitator Support Associate",
    description:
      "Help spread awareness about our courses and earn while promoting quality education across your network.",
    icon: Globe,
    iconBg: "bg-blue-100",
    buttonText: "Get Promoted to Facilitator",
    buttonColor: "bg-blue-500 hover:bg-blue-600",
    features: [
      "Commission-based earnings",
      "Marketing materials provided",
      "Ongoing support & training",
      "Personalized dashboard",
      "Global recognition",
    ],
  },
  {
    title: "Facilitator",
    description:
      "Share your expertise by teaching courses and mentoring students in your area of specialization.",
    icon: Users,
    iconBg: "bg-orange-100",
    // buttonText: "Become Facilitator",
    buttonColor: "bg-orange-500 hover:bg-green-600",
    features: [
      "Competitive compensation",
      "Professional development",
      "Flexible schedule",
      "Personalized dashboard",
      "Global recognition",
    ],
  },
  {
    title: "Partner",
    description:
      "Collaborate with us as an institutional partner to expand educational opportunities and reach more learners.",
    icon: CheckCircle,
    iconBg: "bg-purple-100",
    buttonText: "Partner with Us",
    buttonColor: "bg-purple-500 hover:bg-purple-600",
    features: [
      "Earn Money",
      "Become fulltime partner",
      "Become part-time partner",
      "Strategic collaboration",
      "Mutual growth opportunities",
      "Shared resources & expertise",
      "Personalized dashboard",
      "Global recognition",
    ],
  },
];

const features = [
  {
    title: "High Quality Certifications",
    description:
      "Internationally recognized certificates that advance your career",
  },
  {
    title: "Academic Excellence",
    description: "World-class curriculum delivered by professional handlers",
  },
  {
    title: "Flexible Learning",
    description: "Learn at your own pace from anywhere in the world",
  },
];

export default function AboutUs() {
  const [displayedText, setDisplayedText] = useState("");
  const router = useRouter();
  const fullText =
    "Empowering Africa through world-class online education and professional development programs";

  useEffect(() => {
    let currentIndex = 0;
    const timer = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setDisplayedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(timer);
      }
    }, 50);

    return () => clearInterval(timer);
  }, []);

  return (
    <main>
      <Header />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="bg-[#21205A] text-white min-h-[60vh] flex items-center justify-center px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="flex flex-col gap-2 md:gap-4">
              <h1 className="font-bold text-white text-2xl md:text-4xl leading-tight">
                About La Plage Meta Verse
              </h1>
              <h1
                className="font-bold text-xl md:text-3xl leading-tight max-w-3xl mx-auto min-h-[3rem] md:min-h-[4rem]"
                style={{
                  backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  display: "inline-block",
                }}
              >
                {displayedText}
                <span className="animate-pulse">|</span>
              </h1>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-base mt-8">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>1.5M+ Graduates</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                <span>1000+ Courses</span>
              </div>
              <div className="flex items-center gap-2">
                <BsPeople className="w-4 h-4" />
                <span>5000+ Facilitators</span>
              </div>
              <div className="flex items-center gap-2">
                <BiWorld className="w-4 h-4" />
                <span>55+ Countries</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>99% Satisfaction</span>
              </div>
            </div>
          </div>
        </section>
        {/* About LaPlage Metaverse Section */}
        <section className="py-16 px-4 md:py-32">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-[#21205A] mb-8 text-center">
              Transforming Lives Through Education
            </h2>
            <div>
              <p className="text-center text-gray-600 text-lg md:text-xl mb-6 leading-relaxed">
                La Plage Meta Verse is Africa&apos;s leading online education
                platform, dedicated to providing accessible, high-quality
                learning opportunities across the continent. Since our
                inception, we&apos;ve been committed to bridging the educational
                gap and empowering individuals with the skills they need to
                thrive in today&apos;s digital economy.
                <span className="font-medium text-gray-700">
                  We remain the most affordable in the world.
                </span>
              </p>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#1caa1cda] mt-1 flex-shrink-0" />
                    <div className="text-center">
                      <h3 className="font-semibold text-[#21205A]">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mission, Vision & Values Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            {/* Mission & Vision */}
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#21205A] mb-4">
                  Our Mission
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  To democratize access to quality education across Africa by
                  providing world-class online learning experiences that empower
                  individuals with the skills, knowledge, and certifications
                  needed to succeed in the global digital economy.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Eye className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#21205A] mb-4">
                  Our Vision
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  To become Africa&apos;s premier educational technology
                  platform, transforming millions of lives through accessible,
                  innovative, and internationally recognized learning programs
                  that bridge the skills gap and drive economic growth across
                  the continent.
                </p>
              </div>
            </div>

            {/* Core Values */}
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-[#21205A] mb-4">
                Our Core Values
              </h3>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                These fundamental principles guide everything we do and shape
                our commitment to educational excellence and student success.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreValues.map((value, idx) => {
                const Icon = value.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-[#EFAE33] to-[#B97BFF] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-[#21205A] mb-3">
                      {value.title}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Course Timeline Section */}
        <section className="py-16 px-4 ">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-[#21205A] mb-12 text-center">
              Your Journey to Certification
            </h2>
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200"></div>
              <div className="space-y-12">
                <div className="relative flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg relative z-10">
                    01
                  </div>
                  <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <User className="w-6 h-6 text-blue-500" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        Register & Enroll
                      </h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Sign up on our platform and browse through 1000+
                      professional courses across various categories including
                      Health & Safety, Digital Skills, and International
                      Development.
                    </p>
                  </div>
                </div>

                <div className="relative flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg relative z-10">
                    02
                  </div>
                  <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <BookOpen className="w-6 h-6 text-blue-500" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        Choose Your Learning Path
                      </h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Select from self-paced programs or join live classes. Our
                      flexible approach lets you learn at your own pace from the
                      comfort of your home.
                    </p>
                  </div>
                </div>

                <div className="relative flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg relative z-10">
                    03
                  </div>
                  <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Users className="w-6 h-6 text-blue-500" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        Learn from Experts
                      </h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Attend classes delivered by our 2000+ leading digital
                      facilitators in over 12 countries and have a world-class
                      learning experience.
                    </p>
                  </div>
                </div>

                <div className="relative flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg relative z-10">
                    04
                  </div>
                  <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-6 h-6 text-blue-500" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        Earn Your Certificate
                      </h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Complete assessments and receive internationally
                      recognized certificates that validate your new skills and
                      boost your career prospects.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Join Our Community Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-[#21205A] mb-12 text-center">
              Join Our Global Community
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {communityRoles.map((role, idx) => {
                const Icon = role.icon;
                const isFacilitator = idx === 3;

                return (
                  <div
                    key={idx}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow flex flex-col"
                  >
                    <div
                      className={`w-16 h-16 ${role.iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}
                    >
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#21205A] mb-3">
                      {role.title}
                    </h3>
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                      {role.description}
                    </p>
                    <div className="space-y-2 mb-6 mt-3 flex-grow">
                      <h3 className="font-semibold">Benefits:</h3>
                      {role.features.map((feature, fIdx) => (
                        <div
                          key={fIdx}
                          className="flex items-center gap-2 text-sm text-gray-500"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    {role.buttonText && (
                      <div className="mt-auto">
                        {!isFacilitator && (
                          <p className="flex items-center justify-center text-base mb-1 text-gray-500">
                            Career Progression{" "}
                            <span>
                              <ArrowBigRight />
                            </span>
                          </p>
                        )}
                        <button
                          className={`w-full text-white py-2 px-4 rounded-md transition-colors text-sm font-medium ${role.buttonColor}`}
                        >
                          {role.buttonText}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Brands Section */}
        <div className="container mx-auto py-16 px-4">
          <div className="flex flex-col gap-5">
            <h3 className="font-bold text-2xl md:text-3xl lg:text-4xl text-center">
              Members, Affiliations, and Partners
            </h3>
            <Swiper
              slidesPerView={3}
              centeredSlides={true}
              loop={true}
              navigation
              autoplay={{
                delay: 2500,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 4,
                  spaceBetween: 30,
                },
                1024: {
                  slidesPerView: 5,
                  spaceBetween: 40,
                },
              }}
              modules={[Pagination, Autoplay, Navigation]}
              className="testimonial-swiper px-4 pb-12 w-full"
            >
              {brands.map((data) => (
                <SwiperSlide key={data.id} className="py-14">
                  <div className="flex items-center justify-center my-auto">
                    <Image
                      src={data.image}
                      alt={data.alt}
                      width={data.width}
                      height={data.height}
                      className="max-w-full h-auto"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-[#21205A] min-h-[60vh] flex items-center">
          <div className="max-w-6xl mx-auto text-center">
            <h1
              className="font-bold text-xl  md:text-3xl  mb-6"
              style={{
                backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                display: "inline-block",
              }}
            >
              Ready to Transform Your Future?
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              Join over 1.5 million graduates who have advanced their careers
              with La Plage Meta Verse. Start your learning journey today and
              unlock endless possibilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  router.push(`https://student.laplagemetaverse.org/sign-in`);
                }}
                className="bg-white text-[#21205A] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Browse Courses
              </button>
              <button
                onClick={() => {
                  router.push(`https://student.laplagemetaverse.org/sign-up`);
                }}
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-[#21205A] transition-colors"
              >
                Get Started Free
              </button>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-8 text-blue-100 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>Quality Certifications</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>Instant Access</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>Flexible Learning</span>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
}
